import { api } from "@/redux/api";

import {
	AnalysisStatusResponse,
	AnalysisStatusResponseSchema,
	CeoSplitInfo,
	CeoSplitInfoSchema,
	CreateCeoSplitRequest,
	CreateCeoSplitSchema,
	OnboardingSampleResultsResponse,
	OnboardingSampleResultsResponseSchema,
	OnboardingTestChange,
	OnboardingTestChangeSchema,
	OnboardingTestResultsResponse,
	OnboardingTestResultsResponseSchema,
	OnboardingTestSuggestion,
	OnboardingTestSuggestionSchema,
	OnboardingTestSuggestionsResponse,
	OnboardingTestSuggestionsResponseSchema,
	ReviewOnboardingTestSuggestionSchema,
	ScoreTestSuggestionReviewAction,
	UpdateOnboardingTestResultRequest,
	UpdateOnboardingTestResultSchema,
} from "./scoreOnboardingTest.schema";

/**
 * RTK Query endpoints for the onboarding-tour "Lead Scoring → Testing"
 * sub-stage (PREC-3700). All endpoints live under `score/onboarding-test`
 * (OnboardingScoringTestController). Components read the responses via the
 * scoreOnboardingTest slice selectors.
 */
export const scoreOnboardingTestApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getOnboardingTestResults: builder.query<OnboardingSampleResultsResponse, { limit?: number }>({
			query: (params) => ({
				url: "score/onboarding-test/results",
				params,
				responseSchema: OnboardingSampleResultsResponseSchema,
			}),
		}),
		// The Retry of the sample-unavailable screen: the server clears the sample + its
		// built-stamp and schedules a fresh build, so a plain results refetch (which would
		// keep answering READY-empty) is not enough.
		rebuildOnboardingTestSample: builder.mutation<OnboardingSampleResultsResponse, void>({
			query: () => ({
				url: "score/onboarding-test/rebuild",
				method: "POST",
				responseSchema: OnboardingSampleResultsResponseSchema,
			}),
		}),
		updateOnboardingTestResult: builder.mutation<
			OnboardingTestChange,
			UpdateOnboardingTestResultRequest & { id: number }
		>({
			query: ({ id, ...body }) => ({
				url: `score/onboarding-test/results/${id}`,
				method: "PATCH",
				body,
				requestSchema: UpdateOnboardingTestResultSchema,
				responseSchema: OnboardingTestChangeSchema,
			}),
		}),
		analyzeOnboardingTestChanges: builder.mutation<AnalysisStatusResponse, void>({
			query: () => ({
				url: "score/onboarding-test/analyze",
				method: "POST",
				responseSchema: AnalysisStatusResponseSchema,
			}),
		}),
		getOnboardingTestSuggestions: builder.query<OnboardingTestSuggestionsResponse, void>({
			query: () => ({
				url: "score/onboarding-test/suggestions",
				responseSchema: OnboardingTestSuggestionsResponseSchema,
			}),
		}),
		reviewOnboardingTestSuggestion: builder.mutation<
			OnboardingTestSuggestion,
			{ id: number; action: ScoreTestSuggestionReviewAction }
		>({
			query: ({ id, action }) => ({
				url: `score/onboarding-test/suggestions/${id}`,
				method: "PATCH",
				body: { action },
				requestSchema: ReviewOnboardingTestSuggestionSchema,
				responseSchema: OnboardingTestSuggestionSchema,
			}),
		}),
		applyOnboardingTestSuggestions: builder.mutation<
			OnboardingTestResultsResponse,
			{ limit?: number }
		>({
			query: (params) => ({
				url: "score/onboarding-test/apply",
				method: "POST",
				params,
				responseSchema: OnboardingTestResultsResponseSchema,
			}),
		}),
		getCeoSplit: builder.query<CeoSplitInfo, void>({
			query: () => ({
				url: "score/onboarding-test/ceo-split",
				responseSchema: CeoSplitInfoSchema,
			}),
		}),
		createCeoSplit: builder.mutation<CeoSplitInfo, CreateCeoSplitRequest>({
			query: (body) => ({
				url: "score/onboarding-test/ceo-split",
				method: "POST",
				body,
				requestSchema: CreateCeoSplitSchema,
				responseSchema: CeoSplitInfoSchema,
			}),
			// The split creates a second scoring — the rubric must refetch to show both blocks.
			invalidatesTags: ["ScoreCriteria"],
		}),
	}),
});

export const {
	useGetOnboardingTestResultsQuery,
	useLazyGetOnboardingTestResultsQuery,
	useRebuildOnboardingTestSampleMutation,
	useUpdateOnboardingTestResultMutation,
	useAnalyzeOnboardingTestChangesMutation,
	useGetOnboardingTestSuggestionsQuery,
	useReviewOnboardingTestSuggestionMutation,
	useApplyOnboardingTestSuggestionsMutation,
	useGetCeoSplitQuery,
	useCreateCeoSplitMutation,
} = scoreOnboardingTestApi;
